/**
 * Marketing attribution capture — UTM params + Meta/Google click IDs.
 *
 * Same no-op-if-nothing-to-capture style as adPixels.ts: safe to call on every page load.
 * Captures first-touch (which source originally brought this visitor) and last-touch
 * (most recent campaign, so a later retargeting click still gets credit for the sale)
 * separately, with a 30-day rolling expiry — matches Meta's default click attribution window.
 */
import safeStorage from './safeStorage';
import { STORAGE_KEYS } from './storageKeys';

const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function readUtmFromUrl() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const utm = {
    source: params.get('utm_source') || null,
    medium: params.get('utm_medium') || null,
    campaign: params.get('utm_campaign') || null,
    content: params.get('utm_content') || null,
    term: params.get('utm_term') || null,
  };
  const fbclid = params.get('fbclid') || null;
  const gclid = params.get('gclid') || null;

  // No utm_source but arrived via an ad click — infer source/medium from the click ID,
  // same convention GA4's default channel grouping uses.
  if (!utm.source && fbclid) {
    utm.source = 'facebook';
    utm.medium = utm.medium || 'paid_social';
  } else if (!utm.source && gclid) {
    utm.source = 'google';
    utm.medium = utm.medium || 'cpc';
  }

  return { utm, fbclid, gclid };
}

function hasAnyUtm(utm) {
  return Boolean(utm.source || utm.medium || utm.campaign || utm.content || utm.term);
}

/** Call once at app startup (see index.tsx). No-ops if there's nothing new to capture. */
export function captureAttribution() {
  if (typeof window === 'undefined') return;

  const { utm, fbclid, gclid } = readUtmFromUrl();
  const now = Date.now();
  const existing = safeStorage.getJSON(STORAGE_KEYS.ATTRIBUTION, null);
  const stillValid = existing && existing.expiresAt > now;

  const record = stillValid ? existing : { firstTouch: null, lastTouch: null, fbclid: null, gclid: null };

  if (hasAnyUtm(utm)) {
    const touch = { ...utm, capturedAt: now };
    if (!record.firstTouch) record.firstTouch = touch;
    record.lastTouch = touch;
  } else if (!record.firstTouch && document.referrer && !document.referrer.includes(window.location.hostname)) {
    // No UTM params, but an external referrer — capture as organic/referral first touch.
    const touch = {
      source: new URL(document.referrer).hostname,
      medium: 'referral',
      campaign: null,
      content: null,
      term: null,
      capturedAt: now,
    };
    record.firstTouch = touch;
    record.lastTouch = touch;
  }

  if (fbclid) {
    record.fbclid = fbclid;
    record.fbclidCapturedAt = now;
  }
  if (gclid) record.gclid = gclid;

  record.expiresAt = now + EXPIRY_MS;
  safeStorage.setJSON(STORAGE_KEYS.ATTRIBUTION, record);
}

/** Current attribution record, or null if nothing has been captured yet. */
export function getAttribution() {
  if (typeof window === 'undefined') return null;
  const record = safeStorage.getJSON(STORAGE_KEYS.ATTRIBUTION, null);
  if (!record || record.expiresAt <= Date.now()) return null;
  return record;
}

/** Meta browser ID (_fbp cookie) — set automatically by the Meta Pixel script. */
export function getFbp() {
  return readCookie('_fbp');
}

/**
 * Meta click ID cookie (_fbc) — set automatically by the Pixel script when present.
 * Falls back to constructing it from our own captured fbclid per Meta's documented format,
 * in case the Pixel script hadn't loaded yet when the visitor first landed.
 */
export function getFbc() {
  const cookie = readCookie('_fbc');
  if (cookie) return cookie;

  const record = getAttribution();
  if (!record?.fbclid) return null;
  return `fb.1.${record.fbclidCapturedAt || Date.now()}.${record.fbclid}`;
}
